import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, approvalsAPI } from '../api/client';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { isDepartmentManager } from '../utils/roleHelpers';
import PlatformDashboard from './PlatformDashboard';
import { getCurrencySymbol } from '../components/CurrencyDisplay';
import SkeletonLoader from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import {
  LayoutDashboard, FileText, Clock, CheckCircle2, Coins, RefreshCw,
  AlertTriangle, Upload, Receipt, Car, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, AlertCircle, Send, ChevronRight,
  Shield, PiggyBank, Building2, Users, BarChart3, Sparkles,
  Wallet
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────
function getStatusBadgeClass(status) {
  const classes = {
    Draft: 'badge-gray', New: 'badge-gray',
    Pending: 'badge-warning', 'Pending Approval': 'badge-warning',
    Approved: 'badge-success',
    Rejected: 'badge-danger',
    Posted: 'badge-info',
  };
  return classes[status] || 'badge-gray';
}

function fmtShort(v, sym = '£') {
  const n = parseFloat(v) || 0;
  if (n >= 1_000_000) return sym + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return sym + (n / 1_000).toFixed(1) + 'K';
  return sym + n.toFixed(2);
}

// ── Shared KPI Card ───────────────────────────────────────────────
function KPICard({ title, value, subtitle, icon: Icon, color = 'blue', trend, urgent, link, onClick }) {
  const bg = { blue: 'bg-blue-50', yellow: 'bg-yellow-50', green: 'bg-green-50', red: 'bg-red-50', purple: 'bg-purple-50', orange: 'bg-orange-50', teal: 'bg-teal-50' };
  const ic = { blue: 'text-blue-600', yellow: 'text-yellow-600', green: 'text-green-600', red: 'text-red-600', purple: 'text-purple-600', orange: 'text-orange-600', teal: 'text-teal-600' };
  const border = urgent ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200';

  const inner = (
    <div className={`bg-white rounded-xl border ${border} shadow-sm p-5 hover:shadow-md transition-all duration-200 ${link || onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">{title}</p>
          <p className={`text-2xl font-bold mt-1.5 ${urgent ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend).toFixed(1)}% vs last month
            </div>
          )}
        </div>
        <div className={`w-11 h-11 rounded-xl ${bg[color] || bg.blue} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${ic[color] || ic.blue}`} />
        </div>
      </div>
    </div>
  );

  if (link) return <Link to={link}>{inner}</Link>;
  if (onClick) return <div onClick={onClick}>{inner}</div>;
  return inner;
}

// ── Small invoice/expense row ─────────────────────────────────────
function ItemRow({ title, subtitle, amount, status, onClick, urgencyDays }) {
  const isUrgent = urgencyDays !== undefined && urgencyDays <= 2;
  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between p-3 rounded-lg border transition-colors
        ${isUrgent ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}
        ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="min-w-0">
        <p className="font-medium text-gray-900 text-sm truncate">{title}</p>
        <p className="text-xs text-gray-500 truncate">{subtitle}</p>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        {amount && <p className="font-semibold text-gray-900 text-sm">{amount}</p>}
        {status && <span className={`badge text-xs ${getStatusBadgeClass(status)}`}>{status}</span>}
        {urgencyDays !== undefined && (
          <p className={`text-xs mt-0.5 ${isUrgent ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
            {urgencyDays <= 0 ? 'Overdue' : `Due in ${urgencyDays}d`}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Quick Action button ───────────────────────────────────────────
function QuickAction({ icon: Icon, label, color, link }) {
  const cls = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-emerald-600 hover:bg-emerald-700',
    purple: 'bg-violet-600 hover:bg-violet-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
  };
  return (
    <Link to={link} className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors shadow-sm ${cls[color] || cls.blue}`}>
      <Icon className="w-4 h-4" />
      {label}
    </Link>
  );
}

// ── ROLE DASHBOARDS ───────────────────────────────────────────────

/** Finance Administrator view */
function FinanceAdminDashboard({ summary, recentInvoices, pendingApprovals, loading, onRefresh }) {
  const navigate = useNavigate();
  const sym = getCurrencySymbol(summary?.defaultCurrency || 'GBP');
  const ocrExceptions = recentInvoices.filter(i => i.Status === 'New' || i.Status === 'Draft').length;
  const awaitingCoding = (summary?.invoices?.PendingCount || 0);
  const postToFinance = summary?.invoices?.ApprovedCount || 0;

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="OCR Exceptions" value={ocrExceptions} subtitle="Invoices needing review" icon={Sparkles}
          color="orange" urgent={ocrExceptions > 0} link="/invoices?status=New" />
        <KPICard title="Awaiting Coding" value={awaitingCoding} subtitle="Pending assignment" icon={FileText}
          color="yellow" urgent={awaitingCoding > 5} link="/invoices?status=Pending" />
        <KPICard title="Ready to Post" value={postToFinance} subtitle="Approved invoices" icon={Send}
          color="green" link="/invoices?status=Approved" />
        <KPICard title="Total Value (Month)" value={fmtShort(summary?.invoices?.TotalValue, sym)}
          subtitle="Across all invoices" icon={Coins} color="blue" />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <QuickAction icon={Upload} label="Upload Invoices" color="blue" link="/invoices/upload" />
        <QuickAction icon={BarChart3} label="Finance Analytics" color="purple" link="/finance-dashboard" />
        <QuickAction icon={TrendingUp} label="Budget Overview" color="green" link="/budgets" />
      </div>

      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Recent Invoices</h2>
            <Link to="/invoices" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-4 space-y-2">
            {loading ? <SkeletonLoader type="card" count={1} /> :
              recentInvoices.length > 0 ? recentInvoices.map(inv => (
                <ItemRow key={inv.InvoiceID}
                  title={inv.InvoiceNumber || 'N/A'}
                  subtitle={inv.VendorName}
                  amount={`${sym}${(inv.DisplayAmount || inv.TotalAmount || 0).toFixed(2)}`}
                  status={inv.Status}
                  onClick={() => navigate(`/invoices/${inv.InvoiceID}`)}
                />
              )) : (
                <EmptyState compact icon="invoices" title="No recent invoices"
                  message="Upload your first invoice to begin." action={{ label: 'Upload Invoice', onClick: () => navigate('/invoices/upload') }} />
              )
            }
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Approvals Queue</h2>
            <Link to="/approvals" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-4 space-y-2">
            {loading ? <SkeletonLoader type="card" count={1} /> :
              pendingApprovals.length > 0 ? pendingApprovals.slice(0, 5).map(a => (
                <ItemRow key={a.InvoiceApprovalID}
                  title={a.InvoiceNumber}
                  subtitle={a.VendorName}
                  amount={`${sym}${(a.TotalAmount || 0).toFixed(2)}`}
                  urgencyDays={a.DueDate ? Math.ceil((new Date(a.DueDate) - new Date()) / 86400000) : undefined}
                  onClick={() => navigate(`/invoices/${a.InvoiceID}`)}
                />
              )) : (
                <EmptyState compact icon="invoices" title="No pending approvals" message="All caught up! No invoices waiting for approval." />
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
}

/** Department Manager view */
function DeptManagerDashboard({ summary, pendingApprovals, loading, onRefresh }) {
  const navigate = useNavigate();
  const sym = getCurrencySymbol(summary?.defaultCurrency || 'GBP');

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Pending Approvals" value={pendingApprovals.length}
          subtitle="Invoices awaiting your approval" icon={Clock}
          color="yellow" urgent={pendingApprovals.length > 0} link="/department-approvals" />
        <KPICard title="Approved (Month)" value={summary?.invoices?.ApprovedCount || 0}
          subtitle="Invoices approved" icon={CheckCircle2} color="green" />
        <KPICard title="Dept. Spend (Month)" value={fmtShort(summary?.invoices?.TotalValue, sym)}
          subtitle="Total this month" icon={PiggyBank} color="blue" />
        <KPICard title="Over Budget Lines" value={summary?.overBudgetCount || 0}
          subtitle="Budget lines exceeded" icon={AlertTriangle}
          color="red" urgent={(summary?.overBudgetCount || 0) > 0} link="/budgets" />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <QuickAction icon={CheckCircle2} label="Review Approvals" color="green" link="/department-approvals" />
        <QuickAction icon={PiggyBank} label="Budget Status" color="blue" link="/budgets" />
      </div>

      {/* Pending Approvals List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Invoices Awaiting Your Approval</h2>
          <Link to="/department-approvals" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="p-4 space-y-2">
          {loading ? <SkeletonLoader type="card" count={1} /> :
            pendingApprovals.length > 0 ? pendingApprovals.slice(0, 8).map(a => (
              <ItemRow key={a.InvoiceApprovalID}
                title={a.InvoiceNumber}
                subtitle={`${a.VendorName} · ${a.DepartmentName || ''}`}
                amount={`${sym}${(a.TotalAmount || 0).toFixed(2)}`}
                urgencyDays={a.DueDate ? Math.ceil((new Date(a.DueDate) - new Date()) / 86400000) : undefined}
                onClick={() => navigate(`/invoices/${a.InvoiceID}`)}
              />
            )) : (
              <EmptyState compact icon="invoices" title="No approvals pending"
                message="You're all caught up. No invoices are waiting for your review." />
            )
          }
        </div>
      </div>
    </div>
  );
}

/** Employee / general user view */
function EmployeeDashboard({ loading, onRefresh }) {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [expLoading, setExpLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/expenses/my')
      .then(r => setExpenses(r.data?.expenses || r.data || []))
      .catch(() => setExpenses([]))
      .finally(() => setExpLoading(false));
  }, []);

  const open = expenses.filter(e => ['Draft', 'Submitted', 'Pending', 'Pending Approval'].includes(e.Status));
  const approved = expenses.filter(e => e.Status === 'Approved' || e.Status === 'Paid');
  const rejected = expenses.filter(e => e.Status === 'Rejected');

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard title="Open Claims" value={open.length} subtitle="Submitted or in review"
          icon={Receipt} color="yellow" urgent={open.length > 0} link="/expenses" />
        <KPICard title="Approved Claims" value={approved.length} subtitle="Ready for reimbursement"
          icon={CheckCircle2} color="green" link="/expenses" />
        <KPICard title="Rejected Claims" value={rejected.length} subtitle="Need attention"
          icon={AlertCircle} color="red" urgent={rejected.length > 0} link="/expenses" />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <QuickAction icon={Receipt} label="New Expense" color="blue" link="/expenses/submit" />
        <QuickAction icon={Car} label="Mileage Claim" color="orange" link="/mileage" />
      </div>

      {/* Open Claims */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">My Expense Claims</h2>
          <Link to="/expenses" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="p-4 space-y-2">
          {expLoading ? <SkeletonLoader type="card" count={1} /> :
            expenses.length > 0 ? expenses.slice(0, 6).map(e => (
              <ItemRow key={e.ExpenseID || e.id}
                title={e.Description || e.Title || 'Expense claim'}
                subtitle={new Date(e.ExpenseDate || e.CreatedAt).toLocaleDateString('en-GB')}
                amount={e.TotalAmount != null ? `£${parseFloat(e.TotalAmount).toFixed(2)}` : undefined}
                status={e.Status}
                onClick={() => navigate(`/expenses/${e.ExpenseID || e.id}`)}
              />
            )) : (
              <EmptyState compact icon="expenses" title="No expense claims yet"
                message="Submit your first expense claim to get reimbursed."
                action={{ label: 'New Expense', onClick: () => navigate('/expenses/submit') }} />
            )
          }
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard component ──────────────────────────────────────
export default function Dashboard() {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDeptMgr = isDepartmentManager(user);
  const isFinanceAdmin = hasPermission('manage_finance_settings') || hasPermission('post_invoices');

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [summaryRes, recentRes] = await Promise.all([
        dashboardAPI.getSummary(),
        dashboardAPI.getRecentInvoices(5),
      ]);
      setSummary(summaryRes.data);
      setRecentInvoices(recentRes.data.recentInvoices || []);

      if (hasPermission('approve_invoices') || isDeptMgr) {
        const approvalsRes = await approvalsAPI.getPending();
        setPendingApprovals(approvalsRes.data.pendingApprovals || []);
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // FIX H-6: useEffect must come AFTER fetchDashboardData is defined so the reference is not stale
  // Also add hasPermission and isDeptMgr to deps so dept managers see their pending approvals on load
  useEffect(() => {
    if (!(user?.isSuperAdmin || isSuperAdmin)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isSuperAdmin, isSuperAdmin]);

  // If platform owner, show platform dashboard
  if (user?.isSuperAdmin || isSuperAdmin) {
    return <PlatformDashboard />;
  }

  // Role label for greeting
  const roleLabel = isFinanceAdmin ? 'Finance Administrator'
    : isDeptMgr ? 'Department Manager'
    : 'Employee';

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.firstName || user?.displayName?.split(' ')[0] || user?.username || 'there';

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {greeting()}, {firstName} 👋
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {roleLabel} Dashboard — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading && !summary ? (
        <div className="space-y-6">
          <SkeletonLoader type="stat" count={4} />
          <SkeletonLoader type="card" count={2} />
        </div>
      ) : isFinanceAdmin ? (
        <FinanceAdminDashboard
          summary={summary}
          recentInvoices={recentInvoices}
          pendingApprovals={pendingApprovals}
          loading={loading}
          onRefresh={fetchDashboardData}
        />
      ) : isDeptMgr ? (
        <DeptManagerDashboard
          summary={summary}
          pendingApprovals={pendingApprovals}
          loading={loading}
          onRefresh={fetchDashboardData}
        />
      ) : (
        <EmployeeDashboard loading={loading} onRefresh={fetchDashboardData} />
      )}
    </div>
  );
}
